import React from 'react';

import { Skeleton, Stack, Typography } from '@mui/material';
import ClickablePhone from 'app/shared/components/ClickablePhone';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { ValueStub } from 'constants/data';
import { FieldInputType, PHONE_FIELD_INPUT_TYPES } from 'constants/fields';
import { PendingChangeKind } from 'constants/pendingChanges';
import { resolvePendingChangeKind } from 'utils/pendingChangeKind';

import PendingChangeWrapper from '../../common/PendingChangeWrapper';
import { InfoIcon } from '../../coreDesignSystem/icons';
import FunctionIcon, { FunctionIconSize } from '../FunctionIcon';

import {
  DataDisplayVariant,
  SkeletonTitleSizeMap,
  SkeletonValueSizeMap,
  VARIANTS_WITH_LARGE_ICON,
} from './DataDisplay.constants';
import { getContentWrapperStyles, getDataDisplayStyles } from './DataDisplay.styles';
import { DataDisplayTestIds } from './DataDisplay.testIds';
import { getCardTitleVariant, getCardValueVariant, getFormattedValue } from './DataDisplay.utils';

const DataDisplay = ({
  icon,
  isAdditionalIcon = false,
  additionalIcon = <InfoIcon className="info-icon" size={16} />,
  iconColor,
  title,
  value = '',
  isModified = false,
  changeKind = null,
  className,
  variant = DataDisplayVariant.STYLE_A,
  isLoading = false,
  noTruncate = false,
  type,
  applicationId = null,
  pendingChangeAuthors = null,
}) => {
  const cardTitleVariant = getCardTitleVariant(variant);
  const skeletonTitleSize = SkeletonTitleSizeMap[variant];
  const cardValueVariant = getCardValueVariant(variant);
  const skeletonValueSize = SkeletonValueSizeMap[variant];
  const isWithLargeIcon = VARIANTS_WITH_LARGE_ICON.includes(variant);
  const isPhoneValue = PHONE_FIELD_INPUT_TYPES.includes(type);

  const formattedValue = getFormattedValue(value, type);
  const pendingChangeKind = isLoading ? null : resolvePendingChangeKind(changeKind, isModified);

  return (
    <Stack
      className={className}
      data-testid={DataDisplayTestIds.getDataDisplayContentId(title)}
      sx={getDataDisplayStyles(noTruncate)}
    >
      {isWithLargeIcon && (
        <FunctionIcon iconColor={iconColor} iconSize={FunctionIconSize.LARGE} isLoading={isLoading}>
          {icon}
        </FunctionIcon>
      )}

      <Stack
        data-testid={DataDisplayTestIds.DATE_RANGE_WRAPPER}
        sx={getContentWrapperStyles({
          isModified: pendingChangeKind === PendingChangeKind.DIRECT,
          variant,
        })}
      >
        <Stack className="title-panel" data-testid={DataDisplayTestIds.TITLE_PANEL}>
          {variant === DataDisplayVariant.STYLE_C && (
            <FunctionIcon
              iconColor={iconColor}
              iconSize={FunctionIconSize.SMALL}
              isLoading={isLoading}
            >
              {icon}
            </FunctionIcon>
          )}
          {isLoading ? (
            // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
            <Skeleton sx={{ fontSize: skeletonTitleSize, width: '100%' }} variant="text" />
          ) : (
            <Typography
              data-truncated
              className="truncate"
              color="text.secondary"
              variant={cardTitleVariant}
            >
              {title}
            </Typography>
          )}
          {isAdditionalIcon && additionalIcon}
        </Stack>

        <PendingChangeWrapper
          isSmall
          authors={pendingChangeAuthors}
          changeKind={pendingChangeKind}
          fieldLabel={title}
          textSelector="span"
        >
          {isLoading ? (
            // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
            <Skeleton sx={{ fontSize: skeletonValueSize, width: '100%' }} variant="text" />
          ) : (
            <Typography
              data-truncated
              className={clsx(!noTruncate && 'truncate', 'text-value')}
              component="p"
              variant={cardValueVariant}
            >
              {isPhoneValue ? (
                <ClickablePhone applicationId={applicationId} value={value} />
              ) : (
                formattedValue || ValueStub.COMMON
              )}
            </Typography>
          )}
        </PendingChangeWrapper>
      </Stack>
    </Stack>
  );
};

DataDisplay.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // A pending Direct change; changeKind takes precedence when given.
  isModified: PropTypes.bool,
  // Why the value is pending: a Direct change is highlighted, a Recalculated one underlined.
  changeKind: PropTypes.oneOf(Object.values(PendingChangeKind)),
  isAdditionalIcon: PropTypes.bool,
  additionalIcon: PropTypes.node,
  iconColor: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(DataDisplayVariant)),
  isLoading: PropTypes.bool,
  noTruncate: PropTypes.bool,
  type: PropTypes.oneOf(Object.values(FieldInputType)),
  // Entity context attached to a call placed from a phone-typed value.
  applicationId: PropTypes.string,
  // Who changed the pending value: shown as author dots while the value is highlighted as modified.
  pendingChangeAuthors: PropTypes.arrayOf(PropTypes.object),
};

export default DataDisplay;
